/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable prefer-arrow-callback */
import React, { useState, useMemo } from 'react';
import { findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardActions, CardContent, Grid, Link, Paper, Typography } from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';

import { Footer, EssentialsUserAgreement, NonProdBanner } from '@/components';
import { useAppContext } from '@/context';
import { useFRCallback } from '@/hooks';
import { avLogoWhite, avBackgroundOrange } from '@/static';

const currentEnv = getSpecificEnv();
const shouldShowNonProdBanner = currentEnv !== 'prod';

const NewUserConfirmation = ({ ...props }) => {
  const { stepToRender, setCurrentStep, formValues: registrationValues = {}, userInvitationDetails } = props;
  const { addMessage, setMessages } = useAppContext();
  const { choiceCallback, choiceCallbackIndex, choiceCallbackChoices } = useFRCallback(stepToRender);
  const navigate = useNavigate();

  const [agreementDisplayed, setAgreementDisplayed] = useState(false);

  const { newUserEnrollment: enrollmentValues } = registrationValues;
  const { firstName, lastName, email, userId } = enrollmentValues;

  const { firstName: inviteFirstName, lastName: inviteLastName, emailAd: inviteEmail } = userInvitationDetails;

  // if userInviteDetails, need to always use those values
  const firstNameToUse = useMemo(() => inviteFirstName || firstName, [inviteFirstName, firstName]);
  const lastNameToUse = useMemo(() => inviteLastName || lastName, [inviteLastName, lastName]);
  const emailToUse = useMemo(() => inviteEmail || email, [inviteEmail, email]);

  const handleNavigation = (navigationType) => {
    setMessages([]);

    const output = stepToRender;
    let didError = false;

    if (navigationType && choiceCallback) {
      // need to get the index of the matching navigationType in the choices array from the choice callback
      const formattedChoices = choiceCallbackChoices.map((choice) => choice.toLowerCase());
      const navigationIndex = findIndex(formattedChoices, (choice) => choice === navigationType);

      // reconstruct the callbacks with the correct navigation index as the input
      if (navigationIndex >= 0) {
        const { payload = {} } = output;
        const { callbacks = [] } = payload;

        const newCallbacks = [...callbacks];

        newCallbacks[choiceCallbackIndex].input[0].value = navigationIndex;

        Object.assign(output.payload.callbacks, [...newCallbacks]);
      } else {
        didError = true;
      }
    } else {
      didError = true;
    }

    if (didError) {
      addMessage('Unable to complete the request. Please try again.');
    } else {
      setCurrentStep({ ...output });
    }
  };

  const contentToDisplay = useMemo(() => {
    const output = agreementDisplayed ? (
      <>
        <CardContent>
          <EssentialsUserAgreement />
        </CardContent>
        <CardActions mt="auto">
          <Button fullWidth color="primary" onClick={() => setAgreementDisplayed(false)}>
            Go Back
          </Button>
        </CardActions>
      </>
    ) : (
      <>
        <CardContent>
          <Box
            sx={{
              minHeight: '350px',
            }}
          >
            <Box
              mb={3}
              sx={{
                textAlign: 'center',
              }}
            >
              <Typography variant="h1" mb={2}>
                Verify Your Information
              </Typography>
              <Typography variant="subtitle1">
                Verify the information below is correct, changing this later will be difficult.
              </Typography>
            </Box>
            <Box mb="auto" mx={3}>
              <Typography variant="subtitle1" mb={2}>
                <Box sx={{ fontWeight: 'bold' }}>Name</Box>
                <Box>
                  {firstNameToUse} {lastNameToUse}
                </Box>
              </Typography>
              <Typography variant="subtitle1" mb={2}>
                <Box sx={{ fontWeight: 'bold' }}>Email Address</Box>
                <Box>{emailToUse}</Box>
              </Typography>
              <Typography variant="subtitle1" mb={2}>
                <Box sx={{ fontWeight: 'bold' }}>User ID</Box>
                <Box>{userId}</Box>
              </Typography>
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              textAlign: 'center',
            }}
          >
            <Typography variant="caption">
              By clicking Create Account, I agree to the{' '}
              <Link
                component="button"
                style={{ fontSize: 'inherit', padding: 'inherit', verticalAlign: 'inherit' }}
                onClick={() => setAgreementDisplayed(true)}
              >
                Availity Essentials User Agreement
              </Link>
              .
            </Typography>
          </Box>
        </CardContent>
        <CardActions mt="auto" sx={{ justifyContent: 'end' }}>
          <Button color="secondary" onClick={() => handleNavigation('back')}>
            Edit Information
          </Button>{' '}
          <Button color="primary" onClick={() => handleNavigation('next')}>
            Create Account
          </Button>
        </CardActions>
      </>
    );

    return output;
  }, [agreementDisplayed]);

  return (
    <Grid
      id="new-user-confirmation-page-grid-container"
      container
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh' }}
    >
      {shouldShowNonProdBanner ? (
        <Grid
          id="nonprod-banner-grid-container"
          display="flex"
          mx={2}
          mt={2}
          justifyContent="center"
          sx={{ marginBottom: { xs: 2, md: 0 } }}
        >
          <NonProdBanner />
        </Grid>
      ) : (
        <Box />
      )}
      <Grid
        id="new-user-confirmation-page-content-grid"
        container
        display="flex"
        flexDirection="row"
        justifyContent="center"
        flexWrap="wrap"
        mt="auto"
      >
        <Paper
          variant="elevation"
          elevation={6}
          sx={{
            width: { xs: '350px', sm: '500px', md: '600px' },
            marginRight: { sm: 2 },
            marginBottom: { xs: 2, md: 0 },
          }}
        >
          {contentToDisplay}
        </Paper>
        <Card
          variant="elevation"
          elevation={6}
          sx={{
            alignItems: 'stretch',
            backgroundImage: `url(${avBackgroundOrange})`,
            backgroundSize: 'cover',
            backgroundClip: 'border-box',
          }}
        >
          <Grid display="flex" flexDirection="column" p={3} justifyContent="space-between" sx={{ height: '100%' }}>
            <Grid display="flex" mb="auto" justifyContent="end">
              <img
                src={avLogoWhite}
                alt="Availity Logo"
                style={{
                  maxWidth: '60%',
                }}
              />
            </Grid>
            <Grid display="flex" mb={2} justifyContent="center">
              <Typography variant="subtitle1">
                <Box component="span" sx={{ fontWeight: 'bold', color: '#ffffff' }}>
                  Already have an account?
                </Box>
              </Typography>
            </Grid>
            <Grid mb="auto">
              <Button
                color="secondary"
                onClick={() => {
                  setMessages([]); // clear out messages on route navigation
                  navigate('/login');
                }}
                sx={{ width: '100%' }}
              >
                Log in.
              </Button>
            </Grid>
          </Grid>
        </Card>
      </Grid>
      <Footer />
    </Grid>
  );
};

NewUserConfirmation.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  formValues: PropTypes.object,
  userInvitationDetails: PropTypes.object,
};

export default NewUserConfirmation;
